import { medusaClient } from '@lib/config'
import {
  useAccountApprovedCheck,
  useAccountRejectedCheck,
  useCustomerIsAdmin,
  useCustomerIsRegional,
  useCustomerProductGroups,
  useCustomerSetUpCheck,
} from '@lib/hooks'
import { Customer } from '@medusajs/medusa'
import { useMutation } from '@tanstack/react-query'
import { useMeCustomer } from 'medusa-react'
import { useRouter } from 'next/router'
import React, { createContext, useCallback, useContext, useState } from 'react'

export enum LOGIN_VIEW {
  SIGN_IN = 'sign-in',
  REGISTER = 'register',
}

interface AccountContext {
  customer?: Omit<Customer, 'password_hash'>
  retrievingCustomer: boolean
  loginView: [LOGIN_VIEW, React.Dispatch<React.SetStateAction<LOGIN_VIEW>>]
  isApproved: boolean
  isRejected: boolean
  isSetUp: boolean
  isAdmin: boolean
  isRegional: boolean
  productVisibilityGroups: ProductVisibilityGroup[]
  checkSession: () => void
  refetchCustomer: () => void
  handleLogout: () => void
}

const AccountContext = createContext<AccountContext | null>(null)

interface AccountProviderProps {
  children?: React.ReactNode
}

const handleDeleteSession = () => {
  return medusaClient.auth.deleteSession()
}

export const AccountProvider = ({ children }: AccountProviderProps) => {
  const {
    customer,
    isLoading: retrievingCustomer,
    refetch,
    remove,
  } = useMeCustomer({ onError: () => {} })

  /** Whether customer's account has been approved by Barnhill or not */
  const { isApproved, loading: loadingAccountApproval } =
    useAccountApprovedCheck(customer)
  const { isRejected, loading: loadingAccountRejection } =
    useAccountRejectedCheck(customer)
  const { isSetUp, loading: loadingCustomerSetUp } =
    useCustomerSetUpCheck(customer)

  const { productVisibilityGroups, loading: loadingProductVisibilityGroups } =
    useCustomerProductGroups(customer)
  const isAdmin = useCustomerIsAdmin(customer)
  const isRegional = useCustomerIsRegional(customer)

  const loginView = useState<LOGIN_VIEW>(LOGIN_VIEW.SIGN_IN)

  const router = useRouter()

  const checkSession = useCallback(() => {
    if (!customer && !retrievingCustomer) {
      router.push('/account/login')
    }
  }, [customer, retrievingCustomer, router])

  const useDeleteSession = useMutation({
    mutationFn: handleDeleteSession,
    mutationKey: ['delete-session'],
  })

  const handleLogout = () => {
    useDeleteSession.mutate(undefined, {
      onSuccess: () => {
        remove()
        loginView[1](LOGIN_VIEW.SIGN_IN)
        router.push('/')
      },
    })
  }

  return (
    <AccountContext.Provider
      value={{
        customer,
        isApproved,
        isRejected,
        isSetUp,
        isAdmin,
        isRegional,
        productVisibilityGroups,
        retrievingCustomer:
          loadingAccountApproval ||
          loadingCustomerSetUp ||
          loadingProductVisibilityGroups ||
          retrievingCustomer,
        loginView,
        checkSession,
        refetchCustomer: refetch,
        handleLogout,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export const useAccount = () => {
  const context = useContext(AccountContext)

  if (context === null) {
    throw new Error('useAccount must be used within a AccountProvider')
  }
  return context
}
